import React, {
  type ComponentPropsWithRef,
  useCallback,
  useEffect,
  useState
} from 'react'
import type { EmblaCarouselType } from 'embla-carousel'
import { css, styled } from 'styled-components';
import { radius, space, tk, tok } from '@sunwebgroup/ui-components';

interface UseDotButtonType {
  selectedIndex: number;
  scrollSnaps: number[];
  onDotButtonClick: (index: number) => void;
}
export const useDotButton = (
  emblaApi: EmblaCarouselType | undefined
): UseDotButtonType => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaApi) return
      emblaApi.scrollTo(index)
    },
    [emblaApi]
  )

  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList())
  }, [])

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onInit(emblaApi)
    onSelect(emblaApi)
    emblaApi.on('reInit', onInit)
    emblaApi.on('select', onSelect)
    return () => {
      emblaApi.off('reInit', onInit)
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi, onInit, onSelect])

  return {
    selectedIndex,
    scrollSnaps,
    onDotButtonClick
  }
}

type PropType = ComponentPropsWithRef<'button'> & {
  $selected?: boolean;
};

export const DotButton: React.FC<PropType> = (props) => {
  const { children, ...restProps } = props
  return (
    <Button type="button" $selected {...restProps} >
      {children}
    </Button>
  )
}

const Button = styled.button<{ $selected: boolean }>`
  -webkit-tap-highlight-color: ${tok('primitives_color_neutral_800')};
  -webkit-appearance: none;
  appearance: none;
  background-color: transparent;
  touch-action: manipulation;
  display: inline-flex;
  text-decoration: none;
  cursor: pointer;
  border: 0;
  padding: 0;
  margin: 0;
  width: ${space('primitives_space_8')};
  height: ${space('primitives_space_8')};
  align-items: center;
  justify-content: center;
  border-radius: ${radius('primitives_corner_radius_full')};

  &:after {
    width: ${space('primitives_space_8')};
    height: ${space('primitives_space_8')};
    border-radius: ${radius('primitives_corner_radius_full')};
    display: flex;
    align-items: center;
    content: '';

  }
  ${({ $selected }) =>
    $selected
      ? css`
        &:after {
          background-color: ${tok('semantics_selectors_neutral_selected_hover_background_color')};
        }
      `
      : css`
        &:after {
          background-color: ${tok('semantics_buttons_tinted_neutral_hover_background_color')};
        }
      `}


`
